import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { IAlertNotification } from '../types/model';
import { useAuth } from './Auth';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  CLEAR_ALERT_NOTIFICATIONS,
  GET_UNREAD_NOTIFICATIONS,
  LIST_ALERT_NOTIFICATIONS,
  MARK_ALERT_NOTIFICATIONS_AS_READ,
  ON_ASSET_ALERT,
  SEARCH_ALERT_NOTIFICATIONS,
} from '../http/apollo-queries';
import { toast } from 'react-toastify';
import { logger } from '../helpers/logger';

interface NotificationsContextType {
  notificationsList: IAlertNotification[];
  loading: boolean;
  search: (searchTerm: string) => void;
  nextToken: string | null | undefined;
  loadMore: () => void;
  unreadNotificationsCount: number;
  markAlertNotificationsAsRead: () => void;
  clearAlertNotifications: () => void;
}

const NotificationsContext = createContext<NotificationsContextType>({
  notificationsList: [],
  loading: false,
  search: () => {},
  nextToken: null,
  loadMore: () => {},
  unreadNotificationsCount: 0,
  markAlertNotificationsAsRead: () => {},
  clearAlertNotifications: () => {},
});

export const useNotifications = () => useContext(NotificationsContext);

export const NotificationsProvider: React.FC = ({ children }) => {
  const [count, setCount] = useState(0);
  const [searchResults, setSearchResults] = useState<
    IAlertNotification[] | null
  >(null);

  const { user } = useAuth();

  const {
    data: { listAlertNotifications } = {},
    loading,
    subscribeToMore,
    fetchMore,
  } = useQuery<{
    listAlertNotifications: {
      items: IAlertNotification[];
      nextToken?: string | null;
    };
  }>(LIST_ALERT_NOTIFICATIONS, {
    variables: {
      limit: 20,
      nextToken: null,
    },
    skip: !user?.profile?.id,
  });

  const { data: { getUnreadAlertNotificationsCount } = {} } = useQuery<{
    getUnreadAlertNotificationsCount: number;
  }>(GET_UNREAD_NOTIFICATIONS, {
    skip: !user?.profile?.id,
    onCompleted: ({ getUnreadAlertNotificationsCount }) => {
      setCount(getUnreadAlertNotificationsCount || 0);
    },
  });

  logger.log(
    'NotificationsContext:notifications',
    listAlertNotifications?.items
  );
  logger.log(
    'NotificationsContext:unreadNotifications',
    getUnreadAlertNotificationsCount
  );

  const loadMore = useCallback(
    function loadMore() {
      return fetchMore({
        variables: {
          nextToken: listAlertNotifications?.nextToken || null,
        },
        updateQuery(prevNotifications, newNotifications) {
          // Merge old and newly fetched results together
          return Object.assign({}, prevNotifications, {
            listAlertNotifications: {
              nextToken:
                newNotifications?.fetchMoreResult?.listAlertNotifications
                  .nextToken || null,
              items: [
                ...prevNotifications.listAlertNotifications.items,
                ...(newNotifications?.fetchMoreResult?.listAlertNotifications
                  .items || []),
              ],
            },
          });
        },
      });
    },
    [listAlertNotifications?.nextToken, fetchMore]
  );

  const [searchNotifications, { loading: loadingSearch }] = useLazyQuery<{
    searchAlertNotifications: { items: IAlertNotification[] };
  }>(SEARCH_ALERT_NOTIFICATIONS, {
    onCompleted: (data) =>
      setSearchResults(data?.searchAlertNotifications?.items || null),
    onError: () => setSearchResults(null),
  });

  useEffect(() => {
    if (!user?.profile?.id) {
      return;
    }

    logger.log('NotificationContext:UserId', user?.id);

    const unsubscribe = subscribeToMore<{
      onAlertNotification: IAlertNotification;
    }>({
      document: ON_ASSET_ALERT,
      variables: {
        owner: user?.id,
      },
      updateQuery(prev, { subscriptionData }) {
        logger.log('NotificationContext:subscriptionData', subscriptionData);

        const newAlert = subscriptionData.data.onAlertNotification;
        if (!newAlert) {
          return prev;
        }

        const exists = prev.listAlertNotifications.items.find(
          ({ id }) => id === newAlert.id
        );
        if (exists) {
          return prev;
        }

        setCount(count + 1);

        return {
          ...prev,
          listAlertNotifications: {
            items: [newAlert, ...(prev?.listAlertNotifications?.items || [])],
            nextToken: prev?.listAlertNotifications?.nextToken || null,
          },
        };
      },
    });
    return () => {
      unsubscribe();
    };
  }, [subscribeToMore, user?.id, user]);

  const search = useCallback((searchTerm) => {
    if (!searchTerm) {
      return setSearchResults(null);
    }

    return searchNotifications({
      variables: {
        term: searchTerm,
      },
    });
  }, []);

  const [
    markAlertNotificationsAsRead,
    { loading: loadingMarkAlertNotificationsAsRead },
  ] = useMutation(MARK_ALERT_NOTIFICATIONS_AS_READ, {
    refetchQueries: [
      {
        query: LIST_ALERT_NOTIFICATIONS,
        variables: {
          limit: 20,
          nextToken: null,
        },
      },
    ],
    onCompleted: () => setCount(0),
    onError: (e) => toast.error(e.message),
  });

  const [clearAlertNotifications, { loading: loadingClear }] = useMutation(
    CLEAR_ALERT_NOTIFICATIONS,
    {
      refetchQueries: [
        {
          query: LIST_ALERT_NOTIFICATIONS,
          variables: {
            limit: 20,
            nextToken: null,
          },
        },
      ],
      onError: (e) => toast.error(e.message),
    }
  );

  const clearAll = useCallback(
    function clearAll() {
      return clearAlertNotifications({
        variables: {
          amount: listAlertNotifications?.items.length ?? 0,
        },
      });
    },
    [clearAlertNotifications, listAlertNotifications?.items.length]
  );

  return (
    <NotificationsContext.Provider
      value={{
        notificationsList: searchResults || listAlertNotifications?.items || [],
        loading: loadingSearch || loading || loadingClear,
        search,
        nextToken: listAlertNotifications?.nextToken,
        loadMore,
        unreadNotificationsCount: count,
        markAlertNotificationsAsRead,
        clearAlertNotifications: clearAll,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
